import {
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
  type VisibilityState,
  type RowSelectionState,
  type OnChangeFn,
  type Row,
  type ExpandedState,
  type ColumnSizingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ArrowUpDown, ChevronLeft, ChevronRight, Columns } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  searchKey?: string
  searchValue?: string
  pageSize?: number
  onRowClick?: (row: TData) => void
  getRowClassName?: (row: TData) => string | undefined
  emptyText?: string
  className?: string
  /** Extra-Klassen für die Pagination-Zeile (z.B. pr-24 wenn ein Floating-Button rechts unten liegt) */
  paginationClassName?: string
  /**
   * Opt-in Mehrfachauswahl. Nur aktiv, wenn gesetzt — ohne diese Props verhält
   * sich die Tabelle exakt wie zuvor (13 andere Seiten nutzen sie unverändert).
   * `getRowId` ist dabei Pflicht, sonst sind die Row-Keys Indizes und die
   * Auswahl verrutscht beim Refetch/Sortieren.
   */
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean)
  getRowId?: (row: TData, index: number, parent?: Row<TData>) => string
  rowSelection?: RowSelectionState
  onRowSelectionChange?: OnChangeFn<RowSelectionState>
  /**
   * Opt-in Eltern-Kind-Zeilen (z.B. Mahnungen unter ihrer Rechnung). Ohne diese
   * Prop bleibt alles wie bisher.
   *
   * WICHTIG — warum echte Sub-Rows und kein flaches Array mit Marker-Zeilen:
   *  - Pagination zaehlt Kindzeilen sonst als volle Zeilen, die Seitengrenze
   *    reisst mitten durch eine Rechnung samt ihren Mahnungen.
   *  - Sortierung wuerde Kinder von ihren Eltern trennen.
   * Mit getSubRows paginiert TanStack nur die Eltern und sortiert Kinder
   * innerhalb ihres Elternteils.
   */
  getSubRows?: (row: TData) => TData[] | undefined
  expanded?: ExpandedState
  onExpandedChange?: OnChangeFn<ExpandedState>
  /**
   * Opt-in: Spaltenbreiten per Maus verstellbar.
   *
   * Verhalten: Solange alle Spalten in den Container passen, werden sie
   * proportional auf die volle Breite gestreckt — kein leerer Streifen rechts,
   * kein Scrollbalken. Zieht jemand eine Spalte darüber hinaus, behält jede
   * Spalte exakt ihre eingestellte Breite und die TABELLE wird breiter als der
   * Container (der Wrapper scrollt dann). Die gezogene Spalte knabbert also
   * nicht an den Nachbarn — wer eine Spalte breit haben will, bekommt sie breit.
   *
   * Doppelklick auf den Ziehgriff setzt eine Spalte auf ihre Standardbreite.
   */
  enableColumnResizing?: boolean
  /**
   * Opt-in: Spalten über ein Menü ein-/ausblendbar. Die Auswahl gilt pro
   * Benutzer — der Aufrufer baut den `storageKey` entsprechend (z.B. mit der
   * User-ID), damit sich Kollegen an einem gemeinsamen Rechner nicht in die
   * Quere kommen.
   *
   * Beschriftung im Menü: `columnDef.meta.label`, sonst ein String-Header,
   * sonst die Spalten-ID. Spalten mit `enableHiding: false` (z.B. die
   * Auswahl-Spalte) tauchen nicht auf; die letzte sichtbare lässt sich nicht
   * ausblenden.
   */
  enableColumnVisibility?: boolean
  /** localStorage-Schlüssel für Breiten und Spaltenauswahl. */
  storageKey?: string
}

export function DataTable<TData, TValue>({
  columns,
  data,
  searchKey,
  searchValue,
  pageSize = 25,
  onRowClick,
  getRowClassName,
  emptyText = 'Keine Eintraege vorhanden',
  className,
  paginationClassName,
  enableRowSelection,
  getRowId,
  rowSelection,
  onRowSelectionChange,
  getSubRows,
  expanded,
  onExpandedChange,
  enableColumnResizing,
  enableColumnVisibility,
  storageKey,
}: DataTableProps<TData, TValue>) {
  // Containerbreite messen: darunter füllen die Spalten den Platz proportional,
  // darüber behalten sie ihre exakte Breite und die Tabelle scrollt.
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  useLayoutEffect(() => {
    if (!enableColumnResizing) return
    const el = wrapperRef.current
    if (!el) return
    const measure = () => setContainerWidth(el.clientWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [enableColumnResizing])

  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    if (!storageKey) return {}
    try {
      return JSON.parse(localStorage.getItem(`dt-cols:${storageKey}`) || '{}')
    } catch {
      return {}
    }
  })
  useEffect(() => {
    if (!storageKey || !enableColumnVisibility) return
    try {
      localStorage.setItem(`dt-cols:${storageKey}`, JSON.stringify(columnVisibility))
    } catch { /* private mode o.ä. */ }
  }, [columnVisibility, storageKey, enableColumnVisibility])

  // Eingestellte Breiten überleben Reload und Seitenwechsel
  const columnSizingRef = useRef<ColumnSizingState>({})
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() => {
    if (!storageKey) return {}
    try {
      const stored = JSON.parse(localStorage.getItem(`dt-sizing:${storageKey}`) || '{}')
      columnSizingRef.current = stored
      return stored
    } catch {
      return {}
    }
  })
  // Nur vom Benutzer gezogene Breiten werden gespeichert — die einmalige
  // Einpassung beim ersten Rendern soll nicht als Einstellung gelten.
  const [userResized, setUserResized] = useState(
    () => Object.keys(columnSizingRef.current).length > 0,
  )
  const handleColumnSizingChange: OnChangeFn<ColumnSizingState> = (updater) => {
    setUserResized(true)
    setColumnSizing(updater)
  }
  useEffect(() => {
    if (!storageKey || !userResized) return
    try {
      localStorage.setItem(`dt-sizing:${storageKey}`, JSON.stringify(columnSizing))
    } catch { /* private mode o.ä. — Breiten gelten dann nur für diese Sitzung */ }
  }, [columnSizing, storageKey, userResized])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    // Nur setzen, wenn der Aufrufer Mehrfachauswahl will — sonst unveraendertes Verhalten
    ...(enableRowSelection ? { enableRowSelection } : {}),
    ...(getRowId ? { getRowId } : {}),
    ...(onRowSelectionChange ? { onRowSelectionChange } : {}),
    // dito fuer Eltern-Kind-Zeilen
    ...(getSubRows ? { getSubRows, getExpandedRowModel: getExpandedRowModel() } : {}),
    ...(onExpandedChange ? { onExpandedChange } : {}),
    // dito fuer verstellbare Spaltenbreiten
    ...(enableColumnResizing
      ? {
          enableColumnResizing: true,
          columnResizeMode: 'onChange' as const,
          onColumnSizingChange: handleColumnSizingChange,
          // verhindert, dass eine Spalte beim Ziehen auf null zusammenfaellt
          defaultColumn: { minSize: 70 },
        }
      : {}),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      globalFilter: searchKey ? undefined : searchValue,
      ...(rowSelection ? { rowSelection } : {}),
      ...(expanded !== undefined ? { expanded } : {}),
      ...(enableColumnResizing ? { columnSizing } : {}),
    },
    // Kindzeilen duerfen die Seitengroesse nicht auffressen — paginiert werden
    // ausschliesslich die Elternzeilen.
    ...(getSubRows ? { paginateExpandedRows: false } : {}),
    globalFilterFn: 'includesString',
    // Polling/Refetch liefert neue Array-Referenzen — Seite darf dabei nicht auf 1 zurückspringen
    autoResetPageIndex: false,
    initialState: {
      pagination: { pageSize },
    },
  })

  // Bei neuer Suche zurück auf Seite 1
  useEffect(() => {
    table.setPageIndex(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue])

  // Wenn Daten schrumpfen und die aktuelle Seite nicht mehr existiert: auf letzte Seite klemmen
  const pageCount = table.getPageCount()
  const pageIndex = table.getState().pagination.pageIndex
  useEffect(() => {
    if (pageIndex > 0 && pageIndex >= pageCount) {
      table.setPageIndex(Math.max(0, pageCount - 1))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageCount, pageIndex])

  // Apply column-specific search filter
  if (searchKey && searchValue !== undefined) {
    const col = table.getColumn(searchKey)
    if (col && col.getFilterValue() !== searchValue) {
      col.setFilterValue(searchValue)
    }
  }

  // Breitenberechnung.
  //
  // Solange die Spalten in den Container passen, werden sie proportional auf die
  // volle Breite gestreckt — kein leerer Streifen rechts. Zieht der Benutzer eine
  // Spalte darüber hinaus, behält JEDE Spalte exakt ihre eingestellte Breite und
  // die Tabelle wird breiter als der Container; der Wrapper scrollt dann. Die
  // gezogene Spalte knabbert also nicht an den anderen.
  //
  // WICHTIG: nur px oder reine Prozente verwenden. `calc()` mit gemischten
  // Einheiten (z.B. `calc((100% - 44px) * 0.4)`) wird von Chrome fuer
  // Tabellenspalten bei `table-layout: fixed` IGNORIERT — die Spalten werden dann
  // stumpf gleich breit verteilt und das Verstellen bleibt wirkungslos.
  // Im Browser gemessen: calc-Variante 315|315|315 statt 392|200|354.
  const leafColumns = enableColumnResizing ? table.getVisibleLeafColumns() : []
  const totalSize = leafColumns.reduce((sum, c) => sum + c.getSize(), 0)

  // EINMALIGE Einpassung: solange niemand selbst gezogen hat, werden die
  // Standardbreiten proportional auf die Containerbreite gestreckt und als
  // Größen uebernommen. Danach gilt reine px-Rechnung — zieht jemand eine Spalte,
  // aendert sich NUR diese, und die Tabelle wird entsprechend breiter.
  const visibleKey = leafColumns.map((c) => c.id).join(',')
  const lastFittedRef = useRef<string | null>(null)
  useLayoutEffect(() => {
    if (!enableColumnResizing || containerWidth <= 0) return
    // Einpassen, solange niemand selbst gezogen hat — und immer dann, wenn sich
    // die Menge der sichtbaren Spalten geaendert hat (Ein-/Ausblenden): sonst
    // klaffte nach dem Ausblenden einer Spalte eine Luecke rechts.
    const columnsChanged = lastFittedRef.current !== null && lastFittedRef.current !== visibleKey
    if (userResized && !columnsChanged) { lastFittedRef.current = visibleKey; return }

    const cols = table.getVisibleLeafColumns()
    const total = cols.reduce((sum, c) => sum + c.getSize(), 0)
    lastFittedRef.current = visibleKey
    if (total <= 0 || Math.abs(total - containerWidth) <= 1) return
    const factor = containerWidth / total
    const next: ColumnSizingState = {}
    cols.forEach((c) => { next[c.id] = Math.round(c.getSize() * factor) })
    setColumnSizing(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerWidth, enableColumnResizing, userResized, visibleKey])

  // Tabellenbreite = Summe der Spalten. Ist das breiter als der Container,
  // scrollt der Wrapper — gewollt: wer eine Spalte breit zieht, bekommt sie breit.
  const tableWidth = enableColumnResizing && totalSize > 0 ? totalSize : undefined

  const widthOf = (column: { getSize: () => number }) => {
    if (!enableColumnResizing || totalSize <= 0) return undefined
    return `${column.getSize()}px`
  }

  // Beschriftung fuer das Spalten-Menue: meta.label, sonst String-Header, sonst ID
  const labelOf = (col: { id: string; columnDef: any }) => {
    const meta = col.columnDef.meta as { label?: string } | undefined
    if (meta?.label) return meta.label
    if (typeof col.columnDef.header === 'string') return col.columnDef.header
    return col.id
  }
  const hideableColumns = enableColumnVisibility
    ? table.getAllLeafColumns().filter((c) => c.getCanHide())
    : []
  const visibleHideableCount = hideableColumns.filter((c) => c.getIsVisible()).length

  return (
    <div className={className}>
      {enableColumnVisibility && hideableColumns.length > 0 && (
        <div className="flex justify-end pb-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Columns className="mr-2 h-4 w-4" />
                Spalten
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Sichtbare Spalten</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {hideableColumns.map((col) => {
                const isVisible = col.getIsVisible()
                // Die letzte sichtbare Spalte darf nicht auch noch verschwinden
                const isLastVisible = isVisible && visibleHideableCount <= 1
                return (
                  <DropdownMenuCheckboxItem
                    key={col.id}
                    checked={isVisible}
                    disabled={isLastVisible}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(value) => col.toggleVisibility(!!value)}
                  >
                    {labelOf(col)}
                  </DropdownMenuCheckboxItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
      <div className="rounded-lg border bg-white" ref={wrapperRef}>
        <Table
          className={cn(enableColumnResizing && 'table-fixed')}
          style={tableWidth ? { width: tableWidth } : undefined}
        >
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-muted/50">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    style={{ width: widthOf(header.column) }}
                    className={cn(
                      'px-4 py-3 text-xs font-semibold uppercase tracking-wider',
                      enableColumnResizing && 'relative overflow-hidden',
                    )}
                  >
                    {header.isPlaceholder ? null : header.column.getCanSort() ? (
                      <button
                        className="flex items-center gap-1 hover:text-foreground max-w-full"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <span className="truncate">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                        <ArrowUpDown className="h-3 w-3 flex-shrink-0" />
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                    {enableColumnResizing && header.column.getCanResize() && (
                      // Sichtbare Trennlinie als Greif-Affordanz: eine unsichtbare
                      // Zone muss man mit der Maus suchen. Die Greiffläche ist mit
                      // 9px deutlich breiter als die gezeichnete 2px-Linie.
                      <span
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        onDoubleClick={() => header.column.resetSize()}
                        onClick={(e) => e.stopPropagation()}
                        title="Ziehen zum Verändern der Breite · Doppelklick setzt zurück"
                        className="group absolute right-0 top-0 z-10 flex h-full w-[9px] cursor-col-resize touch-none select-none items-stretch justify-center"
                      >
                        <span
                          className={cn(
                            'w-[2px] rounded-full transition-all',
                            header.column.getIsResizing()
                              ? 'bg-primary-600 w-[3px]'
                              : 'bg-gray-300 group-hover:bg-primary-500 group-hover:w-[3px]',
                          )}
                        />
                      </span>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-depth={row.depth || undefined}
                  className={cn(onRowClick && 'cursor-pointer', getRowClassName?.(row.original))}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      style={{ width: widthOf(cell.column) }}
                      // overflow-hidden ist bei table-fixed Pflicht: sonst sprengt
                      // langer Inhalt die zugewiesene Spaltenbreite.
                      className={cn('px-4 py-3 text-sm', enableColumnResizing && 'overflow-hidden')}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  {emptyText}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* nur anbieten, wenn wirklich jemand gezogen hat — die automatische
          Einpassung beim ersten Rendern ist keine Einstellung */}
      {enableColumnResizing && userResized && (
        <div className="px-2 pt-2">
          <button
            onClick={() => {
              setColumnSizing({})
              // zurueck in den Automatik-Modus: die Einpassung greift wieder
              setUserResized(false)
              if (storageKey) {
                try { localStorage.removeItem(`dt-sizing:${storageKey}`) } catch { /* egal */ }
              }
            }}
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
          >
            Spaltenbreiten zurücksetzen
          </button>
        </div>
      )}

      {table.getPageCount() > 1 && (
        <div className={cn('flex items-center justify-between px-2 py-4', paginationClassName)}>
          <p className="text-sm text-muted-foreground">
            {table.getFilteredRowModel().rows.length} Eintraege
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              Seite {table.getState().pagination.pageIndex + 1} von {table.getPageCount()}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export function SortableHeader({ column, children }: { column: any; children: React.ReactNode }) {
  return (
    <button
      className="flex items-center gap-1 hover:text-foreground"
      onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
    >
      {children}
      <ArrowUpDown className="h-3 w-3" />
    </button>
  )
}
